import React, { useState } from 'react'
import { Grid, IconButton } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Text1, Caption } from 'ui/shorthands/Texts'
import { LabelWithStatus } from 'ui/shorthands/Labels'
import { SelectOptionsProps } from 'types/med'
import { QuestionTooltipWithLabel } from '../shorthands/QuestionTooltip'

export type StackItemProps = {
  /**
   * 左側カラムのテキスト
   */
  label?: React.ReactNode
  /**
   * 左側カラムのテキストのhelp
   */
  labelHelper?: React.ReactNode
  /**
   * コンテンツ上部に表示される説明文
   */
  description?: React.ReactNode
  /**
   * コンテンツ下部に表示される説明文
   */
  descriptionBelow?: React.ReactNode
  /**
   * 上辺ボーダー
   */

  borderTop?: boolean
  /**
   * 下辺ボーダー
   */
  borderBottom?: boolean
  /**
   * 左側カラムを上合わせに
   */
  alignTop?: boolean | string // TODO: labelなどでpaddingを調整している箇所が残っているので修正する
  /**
   * 右側カラムのpaddingを無しにしたい時
   */
  childNoPadding?: boolean
  /**
   * 必須項目の時に使用
   */
  required?: boolean
  /**
   * 必須項目の時に使用 // TODO:（任意）を表示するための旧設定なので不要になったら削除する
   */
  labelStatus?: SelectOptionsProps
  /**
   * 右側カラムでトグル表示
   */
  onClickToggle?: (show: boolean) => void
  /**
   * トグル表示の中身
   */
  toggleContent?: React.ReactNode
  children?: React.ReactNode
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  toggleIcon: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  borderTop: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  borderBottom: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    padding: theme.spacing(2),
    alignSelf: 'center',
  },
  hasToggle: { paddingRight: theme.spacing(6) },
  noPadding: { padding: 0 },
  description: {
    lineHeight: 1.25,
    display: 'block',
  },
  descriptionTop: {
    fontSize: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  descriptionBelow: {
    fontSize: theme.spacing(1.75),
    paddingTop: theme.spacing(1),
  },
  alignTop: {
    alignSelf: 'flex-start',
  },
  label: {
    lineHeight: 1.25,
    [theme.breakpoints.down('sm')]: {
      fontWeight: 'bold',
    },
  },
}))

/**
 * 左側にタイトルのあるpaddingを持つ積み重ねコンテンツ
 */
export const StackItem: React.FC<StackItemProps> = ({
  label,
  labelHelper,
  required,
  labelStatus,
  children,
  description,
  descriptionBelow,
  borderTop,
  borderBottom,
  alignTop,
  onClickToggle,
  toggleContent,
  childNoPadding,
}: StackItemProps) => {
  const { classes, cx } = useStyles()

  const [showToggle, setShowToggle] = useState(false)

  const labelComponent = (
    <Text1 className={classes.label}>
      <LabelWithStatus
        // TODO:labelStatusは（任意）を表示するための旧設定なので不要になったら削除する
        status={required ? 'required' : labelStatus}
      >
        {label}
      </LabelWithStatus>
    </Text1>
  )

  return (
    <>
      <div
        className={cx(
          classes.root,
          borderTop && classes.borderTop,
          borderBottom && classes.borderBottom
        )}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            sm={3}
            className={cx(classes.content, alignTop && classes.alignTop)}
            style={
              typeof alignTop === 'string'
                ? {
                    paddingTop: alignTop,
                    paddingBottom: alignTop,
                  }
                : undefined
            }
          >
            {label &&
              (labelHelper ? (
                <QuestionTooltipWithLabel label={labelComponent}>
                  {labelHelper}
                </QuestionTooltipWithLabel>
              ) : (
                labelComponent
              ))}
            {toggleContent && (
              <div className={classes.toggleIcon}>
                <IconButton
                  onClick={() => {
                    onClickToggle && onClickToggle(!showToggle)
                    setShowToggle(!showToggle)
                  }}
                  size="large"
                >
                  {showToggle ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </div>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            sm={9}
            className={cx(
              classes.content,
              childNoPadding && classes.noPadding,
              toggleContent ? classes.hasToggle : null
            )}
          >
            {description && (
              <Caption
                className={cx(classes.description, classes.descriptionTop)}
              >
                {description}
              </Caption>
            )}
            {children}
            {descriptionBelow && (
              <Caption
                className={cx(classes.description, classes.descriptionBelow)}
              >
                {descriptionBelow}
              </Caption>
            )}
          </Grid>
        </Grid>
      </div>
      {showToggle && toggleContent}
    </>
  )
}
